import React, { useState, useEffect } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ScreenData } from "../../types/ScreenTypes";
import MakroohWarning from "../elements/MakroohWarning";
import BlankScreen from "../elements/BlankScreen";
import PrayerCountdown from "../elements/PrayerCountdown";
import {
  getNextPrayerTime,
  determineNextPrayer,
  getInProgressPrayerIndex,
} from "../../utils/prayerTimeUtils";
import {
  PRAYER_TYPES,
  PRAYER_SEQUENCE,
  PRAYER_TIME_KEYS,
} from "../../constants/prayerConstants";
import "./LandscapeStyles.css";
import {
  getSunriseTimeDisplay,
  getJummahDisplayInfo,
  formatTimeDisplay,
} from "../../utils/timeUtils";
import { formatHijriDate } from "../../utils/dateUtils";
import useBackgroundImage from "../../hooks/useBackgroundImage";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import DateDisplay from "../elements/DateDisplay";
import { getCurrentTimeInTimezone } from "../../utils/timeUtils"; // Import the utility function
import { getHighlightedPrayerIndex } from "../../utils/prayerTimeUtils";

interface LandscapeLayoutProps {
  masjidName: string;
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes: PrayerTimes | null;
  templateId?: string;
  hasChanges?: boolean;
  onChangesAvailable?: (available: boolean) => void;
  screenData: ScreenData;
  prayerState: PrayerState;
  countdownSeconds?: number; // ✅ Add this prop
  flashingPrayer?: string | null; // Prayer key currently flashing (e.g. "JamDhuhr")
}

function getSpecialTimeDisplay(
  currentTimeStr: string,
  todayTime: string | undefined,
  tomorrowTime: string | undefined,
) {
  // If no value, return null
  if (!todayTime) return null;

  // If time has passed, show tomorrow's if available
  const now = currentTimeStr;
  const [nowH, nowM] = now.split(":").map(Number);
  const [todayH, todayM] = todayTime.split(":").map(Number);

  let isTomorrow = false;
  if (nowH > todayH || (nowH === todayH && nowM >= todayM)) {
    if (tomorrowTime) {
      isTomorrow = true;
    }
  }

  return {
    time: isTomorrow ? tomorrowTime : todayTime,
    isTomorrow,
  };
}

export default function LandscapeLayout({
  masjidName,
  prayerTimes,
  tomorrowPrayerTimes = null,
  templateId = "template1",
  hasChanges,
  onChangesAvailable,
  screenData,
  prayerState,
  countdownSeconds = 0, // ✅ Use the prop, default to 0
  flashingPrayer = null,
}: LandscapeLayoutProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [nextPrayer, setNextPrayer] = useState<number>(0);
  const [currentTimeStr, setCurrentTimeStr] = useState<string>("");
  const currentSalahHighlightColor =
    screenData.currentSalahHighlightColor || "#FF0000";
  const [currentDateStr, setCurrentDateStr] = useState<string>(() => {
    const now = getCurrentTimeInTimezone(screenData.timeZone);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  });

  // Azure Best Practice: Use consistent background handling across layouts
  // Get all styles from useBackgroundImage hook instead of just backgroundStyle
  const { containerStyle, backgroundStyle, overlayStyle, contentStyle } =
    useBackgroundImage(
      screenData.backgroundUrl || undefined,
      screenData.backgroundOverlayType || "gradient", // Default to gradient
      screenData.backgroundOverlayOpacity || 0.5, // Lower default opacity (0.65)
    );

  // Use prayerState from props, not from hook
  const {
    isCountdownActive,
    countdownPrayer,
    isMakroohTime,
    makroohReason,
    makroohSecondsLeft,
    isScreenBlank,
    currentIslamicDate,
  } = prayerState;

  // Format Islamic date
  const formatIslamicDate = (dateValue: any): string => {
    if (typeof dateValue === "string") {
      return dateValue;
    }

    if (dateValue && typeof dateValue === "object") {
      if (dateValue.FormattedDate) {
        return dateValue.FormattedDate;
      }

      const day = (dateValue as any).Day || "";
      const monthName = (dateValue as any).MonthName || "";
      const year = (dateValue as any).Year || "";
      const format = screenData.hijriDateFormat || "dd MMMM yyyy";
      return formatHijriDate(
        day,
        monthName,
        year,
        format,
        screenData.customizedNames,
      );
    }

    return "--";
  };

  const formattedIslamicDate = React.useMemo(() => {
    return formatIslamicDate(currentIslamicDate);
  }, [
    currentIslamicDate,
    screenData.hijriDateFormat,
    screenData.customizedNames,
  ]);

  // Format current date - depends on currentDateStr state which is reliably updated at midnight
  const formattedGregorianDate = React.useMemo(() => {
    const today = getCurrentTimeInTimezone(screenData.timeZone);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return today.toLocaleDateString("en-US", options);
  }, [currentDateStr, screenData.timeZone]);

  // Update time and highlighted prayer
  useEffect(() => {
    function updateTime() {
      const now = getCurrentTimeInTimezone(screenData.timeZone);
      setCurrentTime(now);

      const hours = now.getHours();
      const minutes = now.getMinutes();
      const timeStr = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
      setCurrentTimeStr(timeStr);

      // Update date string - triggers Gregorian date recalculation at midnight
      const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setCurrentDateStr((prev) => {
        if (prev !== newDateStr) {
          console.log(
            `📅 [LandscapeLayout] Date changed: ${prev} → ${newDateStr}`,
          );
          return newDateStr;
        }
        return prev;
      });

      const idx = getHighlightedPrayerIndex(
        prayerTimes,
        tomorrowPrayerTimes,
        screenData,
        timeStr,
        now,
      );
      setNextPrayer(idx); // now means "highlighted index"
    }

    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, [
    prayerTimes,
    tomorrowPrayerTimes,
    screenData.timeZone,
    screenData.fajrLength,
    screenData.dhuhrLength,
    screenData.asarLength,
    screenData.maghribLength,
    screenData.ishaLength,
    screenData.jummahLength,
  ]);

  // Bulletproof date update: Handle page visibility changes (browser tab wake-up, screen on)
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        const now = getCurrentTimeInTimezone(screenData.timeZone);
        const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        setCurrentDateStr((prev) => {
          if (prev !== newDateStr) {
            console.log(
              `📅 [LandscapeLayout] Date updated on visibility: ${prev} → ${newDateStr}`,
            );
            return newDateStr;
          }
          return prev;
        });
        setCurrentTime(now);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [screenData.timeZone]);

  // Bulletproof date update: Periodic sanity check every 60 seconds
  useEffect(() => {
    const sanityCheck = setInterval(() => {
      const now = getCurrentTimeInTimezone(screenData.timeZone);
      const newDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setCurrentDateStr((prev) => {
        if (prev !== newDateStr) {
          console.log(
            `📅 [LandscapeLayout] Date corrected by sanity check: ${prev} → ${newDateStr}`,
          );
          return newDateStr;
        }
        return prev;
      });
    }, 60000);

    return () => clearInterval(sanityCheck);
  }, [screenData.timeZone]);

  // Handle changes notification
  useEffect(() => {
    if (onChangesAvailable && hasChanges) {
      onChangesAvailable(true);
    }
  }, [hasChanges, onChangesAvailable]);

  // If screen should be blank, show the blank screen
  if (isScreenBlank) {
    return (
      <BlankScreen
        message={screenData.salahInProgressMessage}
        hijriDate={formattedIslamicDate}
        gregorianDate={formattedGregorianDate}
        screenData={screenData}
        showDateTime={true}
        backgroundUrl={screenData.blankScreenBackgroundUrl}
        countdownSecondsLeft={prayerState.blankScreenSecondsLeft}
        countdownTotalSeconds={prayerState.blankScreenTotalSeconds}
        countdownPrayer={prayerState.blankScreenPrayer}
      />
    );
  }

  // Example of rendering a time with period styling
  const formatTimeWithPeriod = (timeStr: string, use12Hour: boolean = true) => {
    if (!timeStr || timeStr === "--:--") return timeStr;

    const formatted = formatTimeDisplay(timeStr, use12Hour);

    if (
      !use12Hour ||
      (!formatted.includes("am") && !formatted.includes("pm"))
    ) {
      return formatted; // No period to format
    }

    // Find the position of "am" or "pm"
    const amIndex = formatted.indexOf("am");
    const pmIndex = formatted.indexOf("pm");
    const periodIndex = amIndex >= 0 ? amIndex : pmIndex;

    if (periodIndex >= 0) {
      const timePart = formatted.substring(0, periodIndex);
      const periodPart = formatted.substring(periodIndex, periodIndex + 2);

      return <>{timePart}</>;
    }

    return formatted;
  };

  return (
    <div
      className={`layout-container landscape-layout-container template-${templateId}`}
      style={{
        ...(containerStyle as React.CSSProperties),
        ["--current-salah-highlight-color" as any]: currentSalahHighlightColor,
        ["--theme-secondary-color" as any]: screenData.secondaryColor || "#ffc107",
      }}
    >
      {screenData.backgroundUrl && (
        <div className="background-layer" style={backgroundStyle}></div>
      )}
      {screenData.backgroundUrl && (
        <div className="overlay-layer" style={overlayStyle}></div>
      )}

      <div className="content-layer" style={contentStyle}>
        {/* Header */}
        <div className="header prominent-header">
          <h1>{masjidName}</h1>
        </div>

        {/* Current time and date section */}
        <div className="top">
          <div className="time-display">
            <div className="current-time-display">
              {screenData.use12HourFormat
                ? (() => {
                    const hours = currentTime.getHours();
                    const minutes = currentTime.getMinutes();
                    const hour12 = hours % 12 || 12;
                    const period = hours >= 12 ? "pm" : "am";
                    const formattedTime = `${hour12}:${minutes
                      .toString()
                      .padStart(2, "0")}`;
                    return (
                      <>
                        {formattedTime}:
                        {currentTime.getSeconds().toString().padStart(2, "0")}
                        {/* <span className="period" style={{ fontSize: "60%" }}>
                          {period}
                        </span> */}
                      </>
                    );
                  })()
                : `${currentTime
                    .getHours()
                    .toString()
                    .padStart(2, "0")}:${currentTime
                    .getMinutes()
                    .toString()
                    .padStart(2, "0")}:${currentTime
                    .getSeconds()
                    .toString()
                    .padStart(2, "0")}`}
            </div>
            <div className="date-info">
              <DateDisplay
                hijriDate={formattedIslamicDate}
                gregorianDate={formattedGregorianDate}
              />
            </div>
          </div>
        </div>

        {/* Main prayer times */}
        <div className="prayer-times-container">
          {(() => {
            // Calculate in-progress prayer index once for all cards
            const inProgressIdx = getInProgressPrayerIndex(
              prayerTimes,
              screenData,
              currentTime,
            );

            return [
              {
                name: screenData.customizedNames.fajr || "Fajr",
                prayerType: PRAYER_TYPES.FAJR,
                index: 0,
                isCountdown:
                  isCountdownActive &&
                  countdownPrayer === screenData.customizedNames.fajr,
              },
              {
                ...(() => {
                  const jummahInfo = getJummahDisplayInfo(
                    currentTime,
                    currentTimeStr,
                    prayerTimes,
                    screenData,
                  );
                  const isJummah = jummahInfo.showJummah;

                  const jummahName = isJummah
                    ? jummahInfo.jummahNumber > 1
                      ? `${screenData.customizedNames.jummah || "Jumu'ah"} ${
                          jummahInfo.jummahNumber
                        }`
                      : screenData.customizedNames.jummah || "Jumu'ah"
                    : screenData.customizedNames.dhuhr || "Dhuhr";

                  return {
                    name: jummahName,
                    prayerType: PRAYER_TYPES.DHUHR,
                    index: 1,
                    isCountdown:
                      isCountdownActive &&
                      (countdownPrayer === screenData.customizedNames.dhuhr ||
                        countdownPrayer === screenData.customizedNames.jummah),
                    isJummah: isJummah,
                    jummahInfo: jummahInfo,
                  };
                })(),
              },
              {
                name: screenData.customizedNames.asar || "Asar",
                prayerType: PRAYER_TYPES.ASAR,
                index: 2,
                isCountdown:
                  isCountdownActive &&
                  countdownPrayer === screenData.customizedNames.asar,
              },
              {
                name: screenData.customizedNames.maghrib || "Maghrib",
                prayerType: PRAYER_TYPES.MAGHRIB,
                index: 3,
                isCountdown:
                  isCountdownActive &&
                  countdownPrayer === screenData.customizedNames.maghrib,
              },
              {
                name: screenData.customizedNames.isha || "Isha",
                prayerType: PRAYER_TYPES.ISHA,
                index: 4,
                isCountdown:
                  isCountdownActive &&
                  countdownPrayer === screenData.customizedNames.isha,
              },
            ].map((prayer) => {
              const nextTime = getNextPrayerTime(
                prayer.prayerType,
                prayerTimes,
                tomorrowPrayerTimes,
                currentTimeStr,
              );

              // Override to force today's times if this prayer is in its jamaat window
              if (prayer.index === inProgressIdx) {
                const keys = PRAYER_TIME_KEYS[
                  prayer.prayerType as keyof typeof PRAYER_TIME_KEYS
                ] as { BEG?: string; JAM?: string };

                if (keys) {
                  nextTime.time = keys.BEG
                    ? (prayerTimes[keys.BEG as keyof PrayerTimes] as string)
                    : nextTime.time;
                  nextTime.iqama = keys.JAM
                    ? (prayerTimes[keys.JAM as keyof PrayerTimes] as string)
                    : nextTime.iqama;
                  nextTime.isToday = true;
                }
              }

              // Special handling for Jumu'ah
              if ("isJummah" in prayer && prayer.isJummah) {
                if ("jummahInfo" in prayer && prayer.jummahInfo) {
                  nextTime.iqama = prayer.jummahInfo.jummahTime;
                  nextTime.isToday = true;
                }
              } else if (
                prayer.prayerType === PRAYER_TYPES.DHUHR &&
                !("isJummah" in prayer && prayer.isJummah) &&
                tomorrowPrayerTimes
              ) {
                // If it's Dhuhr but all Jumu'ah prayers have passed, show tomorrow's Dhuhr
                const currentDate = new Date();
                const isFriday = currentDate.getDay() === 5;

                if (isFriday) {
                  const jummahInfo = getJummahDisplayInfo(
                    currentDate,
                    currentTimeStr,
                    prayerTimes,
                  );

                  if (!jummahInfo.showJummah) {
                    nextTime.iqama =
                      tomorrowPrayerTimes.JamDhuhr ||
                      tomorrowPrayerTimes.BegDhuhr;
                    nextTime.isToday = false;
                  }
                }
              }

              const isInProgress = prayer.index === inProgressIdx;

              return (
                <div
                  key={prayer.index}
                  className={`prayer-card${
                    nextPrayer === prayer.index ? " active" : ""
                  }${isInProgress ? " in-progress" : ""}`}
                >
                  <div className="prayer-name-label">{prayer.name}</div>
                  <div className="prayer-begin-time">
                    {prayer.prayerType === PRAYER_TYPES.MAGHRIB ? (
                      <span style={{ visibility: "hidden" }}>--:--</span>
                    ) : nextTime.time ? (
                      formatTimeWithPeriod(
                        nextTime.time,
                        screenData.use12HourFormat,
                      )
                    ) : (
                      "--:--"
                    )}
                  </div>
                  <div
                    className={`prayer-jamaat-time${
                      flashingPrayer &&
                      prayer.prayerType ===
                        {
                          JamFajr: "fajr",
                          JamDhuhr: "dhuhr",
                          JamAsar: "asar",
                          JamEsha: "isha",
                        }[flashingPrayer]
                        ? " salah-change-flash"
                        : ""
                    }`}
                    style={
                      flashingPrayer &&
                      prayer.prayerType ===
                        {
                          JamFajr: "fajr",
                          JamDhuhr: "dhuhr",
                          JamAsar: "asar",
                          JamEsha: "isha",
                        }[flashingPrayer]
                        ? ({
                            "--flash-speed": `${screenData.flashSalahChangesSpeed ?? 1}s`,
                            ...(screenData.flashSalahChangesColor
                              ? { color: screenData.flashSalahChangesColor }
                              : {}),
                          } as React.CSSProperties)
                        : undefined
                    }
                  >
                    {isInProgress ? (
                      <span className="in-progress-time">
                        {formatTimeWithPeriod(
                          nextTime.iqama || "--:--",
                          screenData.use12HourFormat,
                        )}
                      </span>
                    ) : prayer.isCountdown ? (
                      <PrayerCountdown
                        prayerName={prayer.name}
                        seconds={countdownSeconds} // ✅ Use countdownSeconds from props
                        textColor={screenData.countdownTextColor || "#FF0000"}
                      />
                    ) : nextTime.iqama ? (
                      formatTimeWithPeriod(
                        nextTime.iqama,
                        screenData.use12HourFormat,
                      )
                    ) : (
                      "--:--"
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {/* Special times group: Sehri End, Sunrise, Noon, Jummah at the bottom */}
        <div className="special-times-group">
          {/* Sehri End */}
          {prayerTimes.SehriEnd && (
            <div className="special-time-block">
              <div className="name">
                {screenData.customizedNames.sehri || "Sehri End"}
              </div>
              <div className="time" style={{ fontSize: "2vw" }}>
                {(() => {
                  const sehriDisplay = getSpecialTimeDisplay(
                    currentTimeStr,
                    prayerTimes.SehriEnd,
                    tomorrowPrayerTimes?.SehriEnd,
                  );
                  return sehriDisplay ? (
                    <>
                      {formatTimeWithPeriod(
                        sehriDisplay.time || "--:--",
                        screenData.use12HourFormat,
                      )}
                    </>
                  ) : (
                    <>--:--</>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Sunrise */}
          <div className="special-time-block">
            <div className="name">{screenData.customizedNames.sunrise}</div>
            <div className="time" style={{ fontSize: "2vw" }}>
              {(() => {
                const sunriseDisplay = getSunriseTimeDisplay(
                  currentTimeStr,
                  prayerTimes.Sunrise || "--:--",
                  tomorrowPrayerTimes?.Sunrise,
                  screenData.sunriseMakroohPeriod || 0,
                );
                return (
                  <>
                    {formatTimeWithPeriod(
                      sunriseDisplay.time,
                      screenData.use12HourFormat,
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Noon */}
          {prayerTimes.Noon && (
            <div className="special-time-block">
              <div className="name">
                {screenData.customizedNames.noon || "Noon"}
              </div>
              <div className="time" style={{ fontSize: "2vw" }}>
                {(() => {
                  const noonDisplay = getSpecialTimeDisplay(
                    currentTimeStr,
                    prayerTimes.Noon,
                    tomorrowPrayerTimes?.Noon,
                  );
                  return noonDisplay ? (
                    <>
                      {formatTimeWithPeriod(
                        noonDisplay.time || "--:--",
                        screenData.use12HourFormat,
                      )}
                    </>
                  ) : (
                    <>--:--</>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Jummah 1 */}
          {prayerTimes.Jummah1 &&
            prayerTimes.Jummah1 !== "--:--" &&
            prayerTimes.Jummah1 !== "" && (
              <div className="special-time-block">
                <div className="name">
                  {screenData.customizedNames.jummah || "Jumu'ah"}
                </div>
                <div className="time" style={{ fontSize: "2vw" }}>
                  {/* Check if it's a time format or a custom message */}
                  {/^\d{1,2}:\d{2}/.test(prayerTimes.Jummah1)
                    ? formatTimeWithPeriod(
                        prayerTimes.Jummah1,
                        screenData.use12HourFormat,
                      )
                    : prayerTimes.Jummah1}
                </div>
              </div>
            )}

          {/* Jummah 2 */}
          {prayerTimes.Jummah2 &&
            prayerTimes.Jummah2 !== "--:--" &&
            prayerTimes.Jummah2 !== "" && (
              <div className="special-time-block">
                <div className="name">
                  {`${screenData.customizedNames.jummah || "Jumu'ah"} 2`}
                </div>
                <div className="time" style={{ fontSize: "2vw" }}>
                  {/* Check if it's a time format or a custom message */}
                  {/^\d{1,2}:\d{2}/.test(prayerTimes.Jummah2)
                    ? formatTimeWithPeriod(
                        prayerTimes.Jummah2,
                        screenData.use12HourFormat,
                      )
                    : prayerTimes.Jummah2}
                </div>
              </div>
            )}

          {/* Jummah 3 */}
          {prayerTimes.Jummah3 &&
            prayerTimes.Jummah3 !== "--:--" &&
            prayerTimes.Jummah3 !== "" && (
              <div className="special-time-block">
                <div className="name">
                  {`${screenData.customizedNames.jummah || "Jumu'ah"} 3`}
                </div>
                <div className="time" style={{ fontSize: "2vw" }}>
                  {/* Check if it's a time format or a custom message */}
                  {/^\d{1,2}:\d{2}/.test(prayerTimes.Jummah3)
                    ? formatTimeWithPeriod(
                        prayerTimes.Jummah3,
                        screenData.use12HourFormat,
                      )
                    : prayerTimes.Jummah3}
                </div>
              </div>
            )}

          {/* Jummah 4 */}
          {prayerTimes.Jummah4 &&
            prayerTimes.Jummah4 !== "--:--" &&
            prayerTimes.Jummah4 !== "" && (
              <div className="special-time-block">
                <div className="name">
                  {`${screenData.customizedNames.jummah || "Jumu'ah"} 4`}
                </div>
                <div className="time" style={{ fontSize: "2vw" }}>
                  {/* Check if it's a time format or a custom message */}
                  {/^\d{1,2}:\d{2}/.test(prayerTimes.Jummah4)
                    ? formatTimeWithPeriod(
                        prayerTimes.Jummah4,
                        screenData.use12HourFormat,
                      )
                    : prayerTimes.Jummah4}
                </div>
              </div>
            )}
        </div>

        {/* Makrooh warning */}
        {isMakroohTime && (
          <MakroohWarning
            reason={makroohReason}
            customizedNames={{
              sunrise: screenData.customizedNames.sunrise,
              noon: screenData.customizedNames.noon,
              sunset: screenData.customizedNames.sunset,
            }}
            duration={{
              sunrise: screenData.sunriseMakroohPeriod,
              zawwal: screenData.zawwalMakroohPeriod,
              sunset: screenData.sunsetMakroohPeriod,
            }}
            secondsLeft={makroohSecondsLeft}
            showTimer={screenData.showMakroohTimer ?? true}
            countdownLabel={screenData.makroohTimerLabel ?? "Time Remaining:"}
          />
        )}

        {/* <footer className="footer">
          <PoweredBy />
        </footer> */}
      </div>
    </div>
  );
}
